import React, { PropsWithChildren, ReactElement } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

import './BrandProduct.css';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Title, Button, Image, Section, Row, Col } from '../../importCL';
import { useWidthContext } from '../../providers/Width';

interface ProductDetails{
    productName?: string;
    img?: string;
    alttext?: string;
    annualFee?: string;
    disclosureLink?: string;
    disclosureLinkText?: string;
    benefitInfo?: Benefits[];
    ctaLink?: string;
    ctaText?: string;
}
interface Benefits{
    benefitHeader?: string;
    benefitText?: string;
}

interface IBrandProductProps extends MappedComponentProperties {
    headline?: string;
    bgColor?: string;
    productDetails?: ProductDetails[];
    viewAllLink?: string;
    viewAllText?: string;
}

export const BrandProductEditConfig = {
    emptyLabel: 'Brand Product',
    isEmpty: (props: PropsWithChildren<IBrandProductProps>) => {
        return !props || !props.productDetails || props.isInEditor;
    },
};

const BrandProduct = (props: PropsWithChildren<IBrandProductProps>): JSX.Element => {
    const isDark = (props.bgColor === '#00244e' || props.bgColor === '#0a5ec0');
    const { width }  = useWidthContext();

    const getPadding = (amountOfBenefits: number) => {
        const spacingToAdd = amountOfBenefits === 1 ? 'mt-12' : amountOfBenefits === 2 ? 'mt-8' : '';
        return spacingToAdd;
    };

    const feeAndDisclaimer = (item: ProductDetails) => {
        return <>
            <p className={`${isDark ? 'text-white' : ''} mb-0 mt-4 text-center`}>
                {transformHTMLParserLinks(wrapTrademarkSuperscript(item.annualFee))}
            </p>
            <Button
                icon='chevron'
                href={item?.disclosureLink}
                size='small'
                target='_self'
                variant={isDark? 'link-light': 'link'}>
                {transformHTMLParserLinks((item?.disclosureLinkText)) as unknown as ReactElement}
            </Button>
        </>;
    };
    return (
        <div className= 'BrandProduct' style={{ backgroundColor: props.bgColor }}>
            <Section title={transformHTMLParserLinks(props.headline)} horizontalTitle titleClass={`BrandProduct__title ${isDark ? 'text-white' : ''} m-0`}>
                {props.productDetails?.map((item, index) => {
                    return (
                        <React.Fragment key={index}>
                            <Row justify='center'>
                                <Col xs={6} md={12} className='mt-12 mb-4 md:mb-4'>
                                    <Title className={`BrandProduct__prodName ${isDark ? 'text-white' : ''} leading-2r`} align='center' tag='p'>
                                        {transformHTMLParserLinks(item.productName)}
                                    </Title>
                                </Col>
                            </Row>
                            <Row justify='center'>
                                <Col className='flex flex-col md:items-center' xs={6} md={4}>
                                    <Image
                                        loading='lazy'
                                        className='BrandProduct__img'
                                        src={item?.img}
                                        alt={item?.alttext} />
                                    {width >= 768  && 
                               feeAndDisclaimer(item)
                                    }
                                </Col>
                                <Col className={`flex flex-col justify-between items-start md:items-center md:mb-12 ${getPadding(item.benefitInfo?.length)} md:mt-0`} xs={6} md={4}>
                                    <div className={`md:mt-8 md:mb-4 ${item.benefitInfo?.length === 1 ? 'mb-28' : ''}`}>
                                        {
                                            item.benefitInfo?.map((benefit, index) => {
                                                return (
                                                    <React.Fragment key={index}>                                  
                                                        {index !== 0 && <hr className='my-3 md:my-6'/> }
                                                        <Title 
                                                            className={`${isDark ? 'text-white' : ''} text-xl font-bold md:text-center mb-1`}
                                                            align='left' 
                                                            tag='p'>{transformHTMLParserLinks(benefit.benefitHeader)}
                                                        </Title>
                                                        <p className={`${isDark ? 'text-white' : ''} md:text-center mb-0`}>{transformHTMLParserLinks(benefit.benefitText)}</p>
                                    
                                                    </React.Fragment>
                                                );
                                            })
                                        }
                                    </div>
                                    <div className='flex flex-col md:items-center md:mt-0 mt-16'>
                                        {width < 768  && feeAndDisclaimer(item)}
                                        <Button
                                            className='BrandProduct__cta--button'
                                            href={item?.ctaLink}
                                            size='medium'
                                            target='_self'
                                            variant={isDark? 'ghosted-light' : 'ghosted-dark'}>
                                            {transformHTMLParserLinks((item?.ctaText)) as unknown as ReactElement}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </React.Fragment>
                    );
                })}
                <Row justify='center' className='md:pt-4 pt-16'>
                    <Button
                        className= 'BrandProduct__viewAll'
                        icon='chevron'
                        href={props?.viewAllLink}
                        size='medium'
                        target='_self'
                        variant={isDark? 'link-light': 'link'}>
                        {props?.viewAllText}
                    </Button>
                </Row>

            </Section>
        </div>
    );
};

export default BrandProduct;
